import * as React from "react";
import {
  listAssets,
  readSeo,
  saveSeoPage,
  type AdminSeoRow,
  type SeoPageBody,
  type WriteOk,
} from "@/api/admin";
import { ALWAYS_NOINDEX, isPageKey, isRobotsValue, ROBOTS_VALUES } from "@ewn/shared";
import { useAction, useAsync } from "@/lib/useAsync";
import { savedNotice } from "@/lib/saved";
import { useLocaleChoice, useLocales } from "@/lib/useLocales";
import { when } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, Input, Select, Textarea } from "@/components/ui/input";
import { Modal } from "@/components/ui/modal";
import { Notice, Warnings } from "@/components/ui/feedback";
import { PageHeader } from "@/components/ui/page-header";
import { SkeletonRows } from "@/components/ui/skeleton";
import { TBody, TD, TEmpty, TH, THead, TR, TableShell } from "@/components/ui/table";

/**
 * What each page tells a search engine, per language.
 *
 * TWO KINDS OF ANSWER COME BACK FROM A SAVE, and the screen has to keep them
 * apart. A REFUSAL means nothing was written -- a title that is empty, a slug
 * another page already uses, an og:image that was never uploaded, a title or
 * description another live page already has. A WARNING means the row saved and
 * something about it is poor: a title too long for a search result, or a
 * duplicate in a language not yet built.
 * Showing a warning as a failure teaches an operator to press Save twice;
 * showing a refusal as a warning teaches them their edit went in when it did
 * not.
 *
 * NO LENGTH GUIDANCE IS SPELLED OUT HERE. The numbers that decide "too long"
 * live in the worker, and a second copy on this side would be a copy that stops
 * matching without anything failing. The boxes count characters; the sentences
 * come back from the save.
 *
 * CHANGING A SLUG LEAVES A REDIRECT. That is the part of this screen that
 * changes a URL somebody has already linked to, so the old one is kept and the
 * screen shows the list.
 */
export function SeoScreen() {
  const localeList = useLocales();
  const assetList = useAsync(() => listAssets(), []);
  const locales = localeList.locales;
  const [locale, setLocale] = useLocaleChoice(locales);

  const { data, loading, error, reload } = useAsync(
    () => (locale ? readSeo(locale) : Promise.resolve(null)),
    [locale],
  );
  const [editing, setEditing] = React.useState<AdminSeoRow | null>(null);
  /**
   * What the last save did, said on the PAGE: the dialog closes on a clean
   * save, and "the site is rebuilding" said inside it would close with it.
   */
  const [saved, setSaved] = React.useState<string | null>(null);

  const pages = data?.pages ?? [];
  const history = data?.history ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="SEO"
        description="Titles, descriptions and URLs, one set per language."
      >
        <Select
          size="sm"
          aria-label="Language"
          value={locale}
          onChange={(e) => {
            // What was said about the last save is about the OTHER language.
            setSaved(null);
            setLocale(e.target.value);
          }}
        >
          {locales.map((l) => (
            <option key={l.code} value={l.code}>
              {l.name} ({l.code})
            </option>
          ))}
        </Select>
      </PageHeader>

      {localeList.error && <Notice tone="error">{localeList.error}</Notice>}
      {error && <Notice tone="error">{error}</Notice>}
      {saved && editing === null && <Notice tone="success">{saved}</Notice>}

      <Card>
        {loading && pages.length === 0 ? (
          <SkeletonRows rows={6} cols={4} />
        ) : (
          <TableShell>
            <THead>
              <TH>Page</TH>
              <TH>URL</TH>
              <TH>Title</TH>
              <TH>Indexed</TH>
              <TH>Changed</TH>
              <TH />
            </THead>
            <TBody>
              {pages.length === 0 && (
                <TEmpty colSpan={6}>Nothing written for this language yet.</TEmpty>
              )}
              {pages.map((page) => (
                <TR key={page.pageKey}>
                  <TD className="font-mono text-xs">{page.pageKey}</TD>
                  <TD className="font-mono text-xs text-muted-foreground">
                    {/* The home page's slug is the empty string, which would
                        render as nothing at all in a column of URLs. */}
                    {page.slug === "" ? "/" : `/${page.slug}`}
                  </TD>
                  <TD className="max-w-xs truncate">{page.title}</TD>
                  <TD>
                    {page.robots.startsWith("noindex") ? (
                      <Badge color="gray">hidden</Badge>
                    ) : (
                      <Badge color="green">indexed</Badge>
                    )}
                  </TD>
                  <TD className="whitespace-nowrap text-muted-foreground">{when(page.updatedAt)}</TD>
                  <TD>
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSaved(null);
                          setEditing(page);
                        }}
                      >
                        Edit
                      </Button>
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </TableShell>
        )}
      </Card>

      <RedirectsCard history={history} pages={pages} />

      <SeoPageModal
        page={editing}
        assetKeys={(assetList.data?.assets ?? []).map((a) => a.key)}
        onClose={() => setEditing(null)}
        onSaved={(close, answer) => {
          // Reload either way -- the row changed. Closing is the part that
          // depends on whether there is still something to read.
          reload();
          setSaved(savedNotice(answer));
          if (close) setEditing(null);
        }}
      />
    </div>
  );
}

/**
 * Where the old URLs went.
 *
 * `live` is the worker's word for "this redirect is still doing something": the
 * page it points at still exists, and nothing has taken the old slug back. A
 * row that is not live is history rather than a rule, and showing both without
 * saying which is which would leave an operator guessing why a link they
 * remember no longer works.
 *
 * THE DESTINATION IS RESOLVED TO A URL, not left as the page key the worker
 * sends. The first screenshot of this card read "/faq goes to faq", which tells
 * an operator checking a redirect precisely nothing -- the question they came
 * with is "and where does it land now", and the answer is the page's CURRENT
 * slug, which the table above already has.
 */
function RedirectsCard({
  history,
  pages,
}: {
  history: { oldSlug: string; pageKey: string; createdAt: string; live: boolean }[];
  pages: AdminSeoRow[];
}) {
  const slugOf = new Map(pages.map((p) => [p.pageKey, p.slug]));
  if (history.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Old URLs</CardTitle>
        <span className="text-xs text-muted-foreground">
          Kept so a link somebody already shared keeps working.
        </span>
      </CardHeader>
      <TableShell>
        <THead>
          <TH>Old URL</TH>
          <TH>Goes to</TH>
          <TH>Since</TH>
          <TH>State</TH>
        </THead>
        <TBody>
          {history.map((row) => {
            const slug = slugOf.get(row.pageKey);
            return (
            <TR key={row.oldSlug}>
              <TD className="font-mono text-xs">/{row.oldSlug}</TD>
              <TD className="font-mono text-xs">
                {/* Absent when the page is gone, which is exactly the case
                    `live: false` describes -- so the two columns agree rather
                    than one of them inventing a destination. */}
                {slug === undefined ? (
                  <span className="text-muted-foreground">nowhere — {row.pageKey} is gone</span>
                ) : (
                  <span>{slug === "" ? "/" : `/${slug}`}</span>
                )}
              </TD>
              <TD className="whitespace-nowrap text-muted-foreground">{when(row.createdAt)}</TD>
              <TD>
                {row.live ? (
                  <Badge color="green">redirecting</Badge>
                ) : (
                  <Badge color="gray">not in use</Badge>
                )}
              </TD>
            </TR>
            );
          })}
        </TBody>
      </TableShell>
    </Card>
  );
}

/**
 * The two values, LABELLED here and enumerated in `@ewn/shared`.
 *
 * The list used to be written out again. `ROBOTS_VALUES` is what the worker
 * validates against, so deriving the options from it is what stops the select
 * offering a value the save would refuse -- and the labels stay here, because
 * how to say it to an operator is this screen's business and nobody else's.
 */
const ROBOTS_LABEL: Record<(typeof ROBOTS_VALUES)[number], string> = {
  "index,follow": "Search engines may index it",
  "noindex,nofollow": "Keep it out of search",
};

function SeoPageModal({
  page,
  assetKeys,
  onClose,
  onSaved,
}: {
  page: AdminSeoRow | null;
  assetKeys: string[];
  onClose: () => void;
  /**
   * `close` is false when the save had warnings, which are shown in here.
   * `answer` is what the worker said, including whether a build started.
   */
  onSaved: (close: boolean, answer: WriteOk) => void;
}) {
  const [form, setForm] = React.useState<SeoPageBody | null>(null);
  const action = useAction();
  const reset = action.reset;

  React.useEffect(() => {
    if (!page) return;
    setForm({
      slug: page.slug,
      title: page.title,
      metaDescription: page.metaDescription,
      robots: page.robots,
      ogTitle: page.ogTitle,
      ogDescription: page.ogDescription,
      ogImageKey: page.ogImageKey,
      h1: page.h1,
    });
    reset();
  }, [page, reset]);

  if (!page || !form) return null;

  const set = <K extends keyof SeoPageBody>(key: K, value: SeoPageBody[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));

  const isHome = page.pageKey === "home";
  const isArticle = page.pageKey.startsWith("article:");
  // From `@ewn/shared`, not restated here. The worker refuses `index,follow`
  // on these keys outright, so offering the choice is offering a control that
  // always 400s -- and a second copy of the list is one that stops matching
  // the first without anything failing.
  const neverIndexed = (ALWAYS_NOINDEX as readonly string[]).includes(page.pageKey);

  async function submit() {
    if (!page || !form) return;
    /**
     * THE DIALOG STAYS OPEN WHEN THE SAVE HAD SOMETHING TO SAY.
     *
     * Warnings are rendered inside this modal, so closing on every success
     * would show "that description is already on /faq, and the build refuses
     * duplicates" for exactly as long as it takes the dialog to disappear --
     * which is to say, never. The row saved either way; what is in question is
     * whether there is still something for the operator to read.
     *
     * `action.warnings` is state and is not settled yet at this point, so the
     * answer is taken from the response itself.
     */
    // A key this vocabulary does not know is one the worker would refuse, and
    // refusing it here says so without the round trip. It can only happen to a
    // `seo_pages` row left behind by a page removed from `STATIC_PAGE_KEYS`.
    // Bound to a const so the guard actually NARROWS it: a property access is
    // re-read on every mention, so TypeScript will not carry the check across
    // the callback below -- and the cast that silences that complaint is the
    // one that would also silence a real mismatch.
    const pageKey = page.pageKey;
    if (!isPageKey(pageKey)) {
      // SAID, not just refused. A bare `return` here left the operator with a
      // Save button that was neither disabled nor responsive, which is worse
      // than the round trip it was avoiding: before this guard existed the
      // worker answered 400 and the modal rendered the sentence.
      action.fail(`This build does not know the page "${page.pageKey}".`);
      return;
    }

    let answer: WriteOk | undefined;
    const ok = await action.run(async () => {
      answer = await saveSeoPage(pageKey, page.locale, form);
      return answer;
    });
    if (ok && answer) onSaved(!answer.warnings?.length, answer);
  }

  return (
    <Modal
      open
      title={`${page.pageKey} · ${page.locale}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={action.busy}>
            Cancel
          </Button>
          <Button disabled={action.busy} onClick={() => void submit()}>
            {action.busy ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {action.error && <Notice tone="error">{action.error}</Notice>}
        <Warnings warnings={action.warnings} />

        <Field
          label="URL"
          htmlFor="seo-slug"
          hint={
            isHome
              ? "The home page is the site root and has no URL of its own."
              : isArticle
                ? "An article's URL is changed on the article itself."
                : "Changing this keeps the old URL working as a redirect."
          }
        >
          <Input
            id="seo-slug"
            className="font-mono text-xs"
            disabled={isHome || isArticle}
            value={form.slug}
            onChange={(e) => set("slug", e.target.value)}
          />
        </Field>

        <Field label="Title" htmlFor="seo-title" hint={`${form.title.length} characters`}>
          <Input id="seo-title" value={form.title} onChange={(e) => set("title", e.target.value)} />
        </Field>

        <Field
          label="Meta description"
          htmlFor="seo-desc"
          hint={`${form.metaDescription.length} characters`}
        >
          <Textarea
            id="seo-desc"
            rows={3}
            value={form.metaDescription}
            onChange={(e) => set("metaDescription", e.target.value)}
          />
        </Field>

        <Field label="Heading on the page" htmlFor="seo-h1">
          <Input id="seo-h1" value={form.h1} onChange={(e) => set("h1", e.target.value)} />
        </Field>

        <Field
          label="Search engines"
          htmlFor="seo-robots"
          hint={neverIndexed ? "This page is never indexed, whatever is set here." : undefined}
        >
          <Select
            id="seo-robots"
            disabled={neverIndexed}
            value={form.robots}
            onChange={(e) => {
              if (isRobotsValue(e.target.value)) set("robots", e.target.value);
            }}
          >
            {ROBOTS_VALUES.map((value) => (
              <option key={value} value={value}>
                {ROBOTS_LABEL[value]}
              </option>
            ))}
          </Select>
        </Field>

        <div className="border-t border-border-light pt-4">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            When somebody shares this page
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Left empty, the title and description above are used.
          </p>
          <div className="mt-3 space-y-4">
            <Field label="Shared title" htmlFor="seo-og-title">
              <Input
                id="seo-og-title"
                value={form.ogTitle ?? ""}
                onChange={(e) => set("ogTitle", e.target.value || null)}
              />
            </Field>
            <Field label="Shared description" htmlFor="seo-og-desc">
              <Textarea
                id="seo-og-desc"
                rows={2}
                value={form.ogDescription ?? ""}
                onChange={(e) => set("ogDescription", e.target.value || null)}
              />
            </Field>
            <Field
              label="Shared image"
              htmlFor="seo-og-image"
              hint="Uploaded on the Images screen. A key that is not there is refused."
            >
              <Select
                id="seo-og-image"
                value={form.ogImageKey ?? ""}
                onChange={(e) => set("ogImageKey", e.target.value || null)}
              >
                <option value="">No image</option>
                {/* The stored key is offered even when the asset list has not
                    arrived, so opening this dialog can never silently clear a
                    value that is already set. */}
                {(assetKeys.includes(form.ogImageKey ?? "")
                  ? assetKeys
                  : [...(form.ogImageKey ? [form.ogImageKey] : []), ...assetKeys]
                ).map((key) => (
                  <option key={key} value={key}>
                    {key}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </div>
      </div>
    </Modal>
  );
}
